import CKDisplay from "@/components/ckeditor/CKDisplay"
import type { AssetTasked } from "@/models"
import { useCoreDataStore } from "@/stores/coreDataStore"
import type { Instant } from "@/Temporal"
import { css } from "vite-css-in-js"
import { computed } from "vue"
import { defineComponent, inferProps, type ReactiveComponent } from "vue-utils"

interface Props {
	index: number
	incidentTime: Instant
	asset: AssetTasked
}

const displayStyles = css`
	& > h5 {
		font-size: 1.05em;
	}
	& > table {
		width: 100%;

		td,
		th {
			text-align: center;
		}
	}
	& > label {
		margin-top: 0.75rem;
	}
`

const AssetTaskedDisplay: ReactiveComponent<Props> = (props) => {
	const coreDataStore = useCoreDataStore()
	const name = computed(() => {
		if (props.asset.customName) {
			return props.asset.customName
		}
		return coreDataStore.lookupSUR(props.asset.SRUId)?.name ?? ""
	})

	const times = computed(
		(): Record<string, Instant | null> => ({
			Alerted: props.asset.alerted,
			Departed: props.asset.departed,
			"On Scene": props.asset.onScene,
			Returning: props.asset.returning,
			"Pier Heads": props.asset.pierHeads,
			"Back on Station": props.asset.backOnStation,
		})
	)

	function renderTime(time: Instant | null) {
		if (!time) {
			return "-"
		}
		return <time datetime={time.toJSON()}>{time.toUTCDateTimeString()}</time>
	}

	return () => (
		<div class={displayStyles}>
			<h5>
				{props.index + 1}. {name.value}
			</h5>
			<table class="grid-table" style={{ gridTemplateColumns: `repeat(${Object.keys(times.value).length}, 1fr)` }}>
				<thead>
					<tr>
						{Object.keys(times.value).map((time) => (
							<th key={time}>{time}</th>
						))}
					</tr>
				</thead>
				<tbody>
					<tr>
						{Object.entries(times.value).map(([key, value]) => (
							<td key={key}>{renderTime(value)}</td>
						))}
					</tr>
				</tbody>
			</table>
			{!!props.asset.taskingInstructions?.trim() && (
				<label class="w-full">
					Tasking Instructions
					<CKDisplay htmlContent={props.asset.taskingInstructions ?? ""} />
				</label>
			)}
			{!!props.asset.notes?.trim() && (
				<label class="w-full">
					Notes
					<CKDisplay htmlContent={props.asset.notes ?? ""} />
				</label>
			)}
		</div>
	)
}

export default defineComponent(AssetTaskedDisplay, inferProps<Props>())
